(() => {
  if (customElements.get('oilogic-faq-search')) return;

/*
 * <oilogic-faq-search> — live, client-side filter for the FAQ items on the page.
 *
 * Progressive enhancement: the markup is a real <form action="/search">, so with
 * no JS the field falls back to store search. When JS runs and there are FAQ
 * items on the page, typing filters them in place:
 *   - matches against each item's question + answer text
 *   - hides categories whose items are all filtered out
 *   - toggles a "no results" message (text supplied via data-empty-text)
 * Submitting is intercepted (filtering is already live) unless there is nothing
 * to filter, in which case the form submits to store search as a fallback.
 */
class OilogicFaqSearch extends HTMLElement {
  #ctrl = new AbortController();

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-faq-search-input]');
    if (!this.form || !this.input) return;

    this.items = this.#collectItems();
    this.categories = Array.from(document.querySelectorAll('.faq-category'));
    this.list = document.querySelector('.faq__categories');

    // Nothing on the page to filter → leave the native store-search form intact.
    if (this.items.length === 0) return;

    const { signal } = this.#ctrl;
    this.input.addEventListener('input', () => this.#filter(), { signal });
    this.form.addEventListener('submit', (event) => event.preventDefault(), { signal });
  }

  disconnectedCallback() {
    this.#ctrl.abort();
  }

  #collectItems() {
    return Array.from(document.querySelectorAll('.faq-item')).map((el) => ({
      el,
      text: el.textContent.toLowerCase().replace(/\s+/g, ' ').trim(),
    }));
  }

  #filter() {
    const query = this.input.value.toLowerCase().replace(/\s+/g, ' ').trim();
    let matches = 0;

    this.items.forEach(({ el, text }) => {
      const hit = query === '' || text.includes(query);
      el.hidden = !hit;
      if (hit) matches += 1;
    });

    // Hide categories whose items are all filtered out.
    this.categories.forEach((category) => {
      const visible = category.querySelector('.faq-item:not([hidden])');
      category.hidden = !visible;
    });

    this.#toggleEmptyState(query !== '' && matches === 0);
  }

  #toggleEmptyState(show) {
    if (!this.list) return;

    if (show && !this.emptyEl) {
      this.emptyEl = document.createElement('p');
      this.emptyEl.className = 'faq__empty';
      this.emptyEl.setAttribute('role', 'status');
      this.emptyEl.textContent = this.dataset.emptyText || '';
      this.list.append(this.emptyEl);
    }

    if (this.emptyEl) this.emptyEl.hidden = !show;
  }
}

customElements.define('oilogic-faq-search', OilogicFaqSearch);
})();
